/**
 * AchievementsPage — Gamification achievements display
 *
 * Shows XP, level, streak, level progression, and
 * achievement grid with locked/unlocked states.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Trophy, Flame, Star, CheckCircle } from 'lucide-react';

import { useGamification } from '../context/GamificationContext';
import Card from '../components/shared/Card';
import { ACHIEVEMENTS } from '../data/achievements';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.4 },
  }),
};

const AchievementsPage = () => {
  const { xp, level, daysVisited, achievements, LEVELS, levelProgress, xpToNextLevel } =
    useGamification();

  // Count only ids that still exist in the achievement definitions
  const unlockedCount = achievements.filter((id) =>
    ACHIEVEMENTS.some((a) => a.id === id)
  ).length;

  return (
    <>
      <Helmet>
        <title>Achievements | Finding Your Voice</title>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-10 space-y-10">
        <motion.div
          className="text-center rounded-2xl bg-gradient-to-br from-courtyard-gold/10 via-courtyard-parchment to-courtyard-gold/5 border border-courtyard-gold/20 px-6 py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <h1 className="font-display text-3xl md:text-4xl font-bold text-courtyard-stone">
            Achievements
          </h1>
          <p className="mt-2 text-courtyard-stone/80">Track your progress and celebrate your milestones</p>
        </motion.div>

        {/* Overview Stats */}
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-4 gap-4"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={1}
        >
          <Card className="p-4 text-center bg-courtyard-gold/8 border-courtyard-gold/20">
            <Star className="w-6 h-6 mx-auto mb-1 text-courtyard-gold-ink" aria-hidden="true" />
            <p className="text-2xl font-bold text-courtyard-stone">{xp}</p>
            <p className="text-sm text-courtyard-gold-ink">Total XP</p>
          </Card>
          <Card className="p-4 text-center bg-philemon-bg/40 border-courtyard-olive/20">
            <Trophy className="w-6 h-6 mx-auto mb-1 text-courtyard-olive-dark" aria-hidden="true" />
            <p className="text-2xl font-bold text-courtyard-stone">
              {level.number}
            </p>
            <p className="text-sm text-courtyard-olive-dark">{level.name}</p>
          </Card>
          <Card className="p-4 text-center bg-theron-bg/40 border-courtyard-terracotta/20">
            <Flame className="w-6 h-6 mx-auto mb-1 text-courtyard-clay" aria-hidden="true" />
            <p className="text-2xl font-bold text-courtyard-stone">{daysVisited}</p>
            <p className="text-sm text-courtyard-clay">Days in the Courtyard</p>
          </Card>
          <Card className="p-4 text-center bg-xenia-bg/40 border-courtyard-aegean/20">
            <CheckCircle className="w-6 h-6 mx-auto mb-1 text-courtyard-aegean-dark" aria-hidden="true" />
            <p className="text-2xl font-bold text-courtyard-stone">
              {unlockedCount}/{ACHIEVEMENTS.length}
            </p>
            <p className="text-sm text-courtyard-aegean-dark">Unlocked</p>
          </Card>
        </motion.div>

        {/* Level Progression */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={2}
        >
          <h2 className="font-display text-xl font-bold text-courtyard-stone mb-4">
            Level Progression
          </h2>
          <Card className="p-5">
            <div className="flex items-center justify-between text-sm text-courtyard-stone/80 mb-2">
              <span>
                Level {level.number}: {level.name}
              </span>
              <span>{xpToNextLevel > 0 ? `${xpToNextLevel} XP to next level` : 'Max level reached'}</span>
            </div>
            <div className="h-3 bg-courtyard-stone/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-courtyard-gold rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${levelProgress}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
            <div className="flex justify-between mt-4 gap-2 flex-wrap">
              {LEVELS.map((l) => (
                <div
                  key={l.number}
                  className={`text-center text-sm ${
                    l.number <= level.number
                      ? 'text-courtyard-gold-ink font-bold'
                      : 'text-courtyard-stone/80'
                  }`}
                >
                  <p>{l.number}</p>
                  <p>{l.name}</p>
                </div>
              ))}
            </div>
          </Card>
        </motion.section>

        {/* Achievement Grid */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={3}
        >
          <h2 className="font-display text-xl font-bold text-courtyard-stone mb-4">
            All Achievements
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ACHIEVEMENTS.map((achievement) => {
              const unlocked = achievements.includes(achievement.id);

              return (
                <Card
                  key={achievement.id}
                  className={`p-5 ${
                    unlocked
                      ? 'bg-courtyard-gold/5 border-courtyard-gold/20'
                      : 'border-dashed border-courtyard-stone/30 bg-white/40'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl" aria-hidden="true">
                      {unlocked ? achievement.emoji : '🔒'}
                    </span>
                    <div>
                      <h3 className="font-bold text-courtyard-stone">
                        {achievement.name}
                      </h3>
                      <p className="text-sm text-courtyard-stone/80">
                        {achievement.description}
                      </p>
                      <div className="flex items-center gap-3">
                        {achievement.xpReward > 0 && (
                          <span className="text-xs text-courtyard-gold-ink font-medium">
                            +{achievement.xpReward} XP
                          </span>
                        )}
                        <span className={`text-sm font-medium ${unlocked ? 'text-courtyard-olive-dark' : 'text-courtyard-stone/80'}`}>
                          {unlocked ? 'Unlocked' : 'Locked'}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </motion.section>
      </div>
    </>
  );
};

export default AchievementsPage;
